/**
 * Persistent Training Mode banner — pinned to the top of the main content
 * area when Training Mode is ON (spec §1B.Step 1).
 *
 * Includes the §enhancement "Train me on this page" quick action +
 * companion ⌘K/Ctrl+K keyboard shortcut (handled by TrainingRoot).
 */
import { useTraining } from "@/lib/trainingContext";
import { resolveTopicForPath } from "@/lib/trainingRouteMap";
import { useLocation } from "react-router-dom";

export default function TrainingBanner({ onTrainMePressed }) {
  const { enabled, activeTopic, setPickerOpen } = useTraining();
  const { pathname } = useLocation();
  if (!enabled) return null;

  const suggested = resolveTopicForPath(pathname);

  return (
    <div
      data-testid="training-banner"
      style={{
        background: "#0F2942",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        position: "sticky",
        top: 0,
        zIndex: 95,
        color: "#fff",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#1D9E75",
          animation: "trainingBannerPulse 1.2s infinite",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <style>{`@keyframes trainingBannerPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
      <span style={{ fontSize: 13, fontWeight: 500 }}>Training Mode</span>
      <span style={{ fontSize: 12, color: "#8BA5C0", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid="training-banner-topic-label">
        · {activeTopic?.title || "Choose a topic below"}
      </span>

      <button
        type="button"
        onClick={() => onTrainMePressed?.(suggested)}
        data-testid="training-train-me-on-page"
        aria-label="Train me on this page"
        title="Train me on this page (⌘K / Ctrl+K)"
        style={{
          border: "0.5px solid rgba(255,255,255,0.25)",
          background: "rgba(255,255,255,0.06)",
          color: "#fff",
          borderRadius: 6,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        ✦ Train me on this page
        <span style={{ color: "#8BA5C0", marginLeft: 6, fontWeight: 400 }}>⌘K</span>
      </button>

      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        data-testid="training-banner-open-picker"
        style={{
          border: "0.5px solid rgba(255,255,255,0.2)",
          background: "transparent",
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 11,
          color: "#8BA5C0",
          cursor: "pointer",
        }}
      >
        Browse topics
      </button>

      <button
        type="button"
        onClick={() => window.__trainingExit?.()}
        data-testid="training-banner-exit"
        style={{
          border: "0.5px solid rgba(255,255,255,0.2)",
          background: "transparent",
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 11,
          color: "#8BA5C0",
          cursor: "pointer",
        }}
      >
        Exit training
      </button>
    </div>
  );
}
